// Phase 3 Track A — shared flight-time helpers.
// Extracted from App.jsx so SSR pages (index.astro, [...slug].astro) and
// admin code can reuse the same math. App.jsx keeps city-aware wrappers
// (cityLocation, getApproxFlightMin, destFlightLabel, routeFlightMin) on top
// of these primitives.
//
// Note: App.jsx has its own calcFlightMin(fromCity, toCity, aircraftId) with
// a different signature (city+aircraftId vs coord+speed). Lib's calcFlightMin
// is the coordinate-based primitive used by Phase C flight-time tables.

export const DEFAULT_SPEED_KMH = 220;   // conservative fleet median

// 2026-06-05 — ALL hardcoded flight-time assumptions removed (previously
// taxi 3 + approach 3 = 6-min overhead + a 10% headwind margin). Flight time
// is now **pure cruise** by default (distance ÷ speed, nothing added). Any
// taxi/approach/overhead is **operator-defined per aircraft**
// (costModel.overheadMinutes + overheadAffectsPrice) and injected by the
// caller via the `overheadMin` parameter — never hardcoded here. No migration
// of the old 3+3: existing configs lose the hardcoded overhead until the
// operator sets their own.

// Haversine distance (km) between two {lat,lng} points. Null-safe —
// returns null if either point lacks lat or lng.
export function haversineKm(a, b) {
  if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) return null;
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// Parse cruise speed from string like "232 km/h" → 232. Returns 0 on failure.
export function parseSpeed(s) { return parseInt(String(s)) || 0; }

// Flight time (minutes) between two {lat,lng} points. Null if either lacks GPS.
// Pure cruise by default (no hardcoded overhead). Pass `overheadMin` (operator-
// defined taxi/approach for a specific aircraft) to add it; default 0.
export function calcFlightMin(from, to, speedKmh = DEFAULT_SPEED_KMH, overheadMin = 0) {
  const dist = haversineKm(from, to);
  if (dist == null) return null;
  return Math.round((dist / speedKmh) * 60 + (Number(overheadMin) || 0));
}

// Human-readable label like "~25 min", or null if no GPS.
export function flightTimeLabel(from, to, speedKmh = DEFAULT_SPEED_KMH) {
  const m = calcFlightMin(from, to, speedKmh);
  return m != null ? '~' + m + ' min' : null;
}

// Look up a city's primary {lat, lng} from a locations array. For SSR/Node use
// where the App.jsx `cityLocation` (state-based) isn't available.
// Prefers helipad over airport over any enabled location.
export function cityCoords(locations, cityName) {
  const locs = (locations || []).filter(l =>
    l?.enabled !== false && l?.city === cityName && l?.lat != null && l?.lng != null
  );
  return locs.find(l => l.type === 'helipad')
      || locs.find(l => l.type === 'airport')
      || locs[0]
      || null;
}

// Resolve a destination's coordinates with a graceful fallback chain:
//   1. Destination's own lat/lng (if editor set them in Sanity)
//   2. Primary helipad/airport in the city (cityCoords)
// Returns null if neither path yields valid coords.
export function destCoords(dest, locations) {
  if (dest?.lat != null && dest?.lng != null) {
    return { lat: dest.lat, lng: dest.lng, type: 'destination', name: dest.name };
  }
  return cityCoords(locations, dest?.name);
}

// Distance label in kilometers: "~85 km" or "—" (Track 4E).
export function distLabel(km) {
  if (km == null) return "—";
  return "~" + Math.round(km) + " km";
}

// Build a comma-separated flight-time context string for generator prompts.
// "Athens ~30 min, Mykonos ~58 min, Santorini ~54 min"
// Applies the same 10-km proximity filter used in the render tables.
// Returns empty string if no target GPS, no hubs, or all hubs <10km away.
export function buildHubFlightContext(target, hubs) {
  if (!target?.lat || !target?.lng || !Array.isArray(hubs) || !hubs.length) return '';
  const parts = [];
  for (const hub of hubs) {
    const d = haversineKm({ lat: hub.lat, lng: hub.lng }, target);
    if (d == null || d < 10) continue;
    const label = flightTimeLabel({ lat: hub.lat, lng: hub.lng }, target);
    if (!label) continue;
    parts.push(hub.name + ' ' + label);
  }
  return parts.join(', ');
}
